// src/data/actionPlans.js
//
// "Now what" plans — one per performance metric plus the recovery-first path
// nodes. Written in a plain, neutral voice so they read the same to a player,
// a nurse and an underwriter. Each plan is three small, doable steps for the
// week, not a lecture.
//
// Consumed by the Guided Path (dashboard) and the assessment completion
// screen through getActionPlan() / getActionPlanForNode(). Pure data — no
// React, no icons — so it can be edited (or later moved to the CMS) safely.

export const ACTION_PLANS = {
    sleep_perf: {
        title: 'Sleep',
        context: 'Sleep came in as your lowest area. It affects reaction time, mood and recovery more than any other habit, so it is the best place to start.',
        steps: [
            { title: 'Cool, dark room', desc: 'Aim for 15–19°C and as dark as you can make it. A small drop in body temperature helps deep sleep start.' },
            { title: 'Empty your head before bed', desc: "Write tomorrow's list down before you lie down so it stops running in your head." },
            { title: 'Morning light', desc: 'Get 10–15 minutes of daylight within half an hour of waking. It sets your body clock for the night ahead.' }
        ],
        actionText: 'Open the sleep library'
    },
    focus: {
        title: 'Focus',
        context: 'Focus came in as your lowest area. Constant switching between tasks and notifications drains attention faster than hard work does.',
        steps: [
            { title: 'Work in blocks', desc: '50 minutes on one thing, then 10 minutes fully off screens.' },
            { title: 'Clear the desk', desc: 'A tidy workspace takes less mental effort than a cluttered one.' },
            { title: 'Silence the pings', desc: "Turn off non-essential notifications while you're doing your most important work." }
        ],
        actionText: 'Open the focus toolkit'
    },
    anxiety: {
        title: 'Stress',
        context: 'Your stress levels came in high. The aim this week is to give your system regular chances to settle, not to push through.',
        steps: [
            { title: 'Two breaths in, long breath out', desc: 'When it spikes: two short inhales through the nose, one long exhale through the mouth. Repeat three times.' },
            { title: 'Caffeine cut-off', desc: 'No caffeine within 10 hours of bedtime.' },
            { title: 'Pause between tasks', desc: 'Take 60 seconds to breathe before moving to the next meeting or job.' }
        ],
        actionText: 'Open mental wellbeing'
    },
    resilience: {
        title: 'Bouncing back',
        context: 'Resilience came in as your lowest area — how quickly you recover after a setback. It builds with practice, not toughness.',
        steps: [
            { title: 'Name it', desc: 'After a setback, say what happened in one plain sentence. Naming it takes some of the heat out.' },
            { title: 'One next move', desc: 'Pick the single next action, not the whole fix.' },
            { title: 'Recovery counts', desc: 'Rest, food and sleep are part of bouncing back, not a reward for it.' }
        ],
        actionText: 'Open the resilience modules'
    },
    motivation: {
        title: 'Motivation',
        context: 'Motivation came in as your lowest area. It usually dips when the goal feels far away or the reason for it has gone quiet.',
        steps: [
            { title: 'Shrink the goal', desc: 'Set one target you can hit this week, not this season.' },
            { title: 'Reconnect the why', desc: "Write one line on why this matters to you. Keep it where you'll see it." },
            { title: 'Start small on flat days', desc: 'Do five minutes. Starting is usually the hard part.' }
        ],
        actionText: 'Open the motivation modules'
    },
    confidence: {
        title: 'Confidence',
        context: 'Confidence came in as your lowest area. It tends to follow evidence, so the plan is to collect some.',
        steps: [
            { title: 'Keep a wins list', desc: 'Each day, note one thing you did well, however small.' },
            { title: 'Prepare one thing properly', desc: 'Being ready for a specific moment builds confidence faster than general positivity.' },
            { title: 'Watch the self-talk', desc: "Notice when you'd never say it to someone else. Reword it." }
        ],
        actionText: 'Open the confidence modules'
    },
    discipline: {
        title: 'Consistency',
        context: "Self-discipline came in as your lowest area — following through when you don't feel like it. Routines do most of the work here.",
        steps: [
            { title: 'Same time, same place', desc: 'Attach the habit to something you already do every day.' },
            { title: 'Make it easy to start', desc: 'Lay things out the night before so the first step is already done.' },
            { title: 'Track it', desc: 'A simple tick on a calendar makes streaks visible and harder to break.' }
        ],
        actionText: 'Open the self-discipline modules'
    },
    awareness: {
        title: 'Self-awareness',
        context: "Self-awareness came in as your lowest area — noticing how you're doing before it shows up in your results.",
        steps: [
            { title: 'Two-minute check-in', desc: 'Once a day, ask: energy, mood, stress — each out of ten.' },
            { title: 'Spot the pattern', desc: 'After a week, look for what tends to come before your good and bad days.' },
            { title: 'Ask one person', desc: "Ask someone you trust what they notice when you're under pressure." }
        ],
        actionText: 'Open the self-awareness modules'
    },
    adaptability: {
        title: 'Adaptability',
        context: 'Adaptability came in as your lowest area — how you handle change and things not going to plan.',
        steps: [
            { title: 'Plan B in advance', desc: "For the next big thing, decide what you'll do if it changes." },
            { title: 'Control the controllables', desc: "List what you can and can't change. Put your effort on the first column." },
            { title: 'Debrief the surprise', desc: "When something changes, note what you did well and what you'd do differently next time." }
        ],
        actionText: 'Open the adaptability modules'
    },
    nutrition: {
        title: 'Fuel',
        context: 'Nutrition came in as your lowest area. Small, regular changes matter more than a perfect diet.',
        steps: [
            { title: "Eat before you're empty", desc: "Don't go more than four hours without something, especially on busy days." },
            { title: 'Water first', desc: 'A glass of water with each meal and one when you wake up.' },
            { title: 'One added, not one removed', desc: 'Add a vegetable or protein to a meal you already eat rather than cutting things out.' }
        ],
        actionText: 'Open the nutrition library'
    },
    injury_mgmt: {
        title: 'Pain and recovery',
        context: 'Pain or injury management came in as your lowest area. The aim is to manage it well, not to ignore it.',
        steps: [
            { title: 'Track it', desc: 'Note pain out of ten each day and what you did. Patterns tell you what helps.' },
            { title: 'Follow the plan', desc: 'If you have rehab or treatment advice, do the boring parts consistently.' },
            { title: 'Speak up early', desc: 'Tell someone — a physio, doctor or coach — before it becomes a bigger problem.' }
        ],
        actionText: 'Open the pain and injury library'
    }
};

// Plans for the recovery-first nodes (shown first when Running Hot / At Risk).
// Keyed by the node ids in src/data/guidedPathRoutes.js.
export const RECOVERY_PLANS = {
    'recovery-reset': {
        title: 'Reset',
        context: 'Your results say output is high but the tank is low. This week is about taking some load off, not adding more.',
        steps: [
            { title: 'One thing off the list', desc: 'Drop, delay or hand off one task this week. Actually remove it, not just plan to.' },
            { title: 'Ten quiet minutes', desc: 'Once a day, no screen, no task. A walk, a stretch or just sitting counts.' },
            { title: 'Tell one person', desc: "Let someone know you're running on empty. Saying it out loud makes it easier to act on." }
        ],
        actionText: 'Open Reset & Recharge'
    },
    'recovery-sleep': {
        ...{},
        title: 'Protect your sleep',
        context: 'Recovery starts with tonight. Protecting sleep is the quickest way to get some capacity back.',
        steps: [
            { title: 'Fixed wake time', desc: 'Same wake-up time every day this week, including days off.' },
            { title: 'Screens off 30 minutes before', desc: 'Swap the phone for something low-key in the last half hour.' },
            { title: 'Cool, dark room', desc: 'Aim for 15–19°C and as dark as you can make it.' }
        ],
        actionText: 'Open the sleep library'
    }
};

// Used when a metric has no dedicated plan (future metrics) — still three
// concrete steps rather than a dead end.
export const genericPlan = (label = 'this area') => ({
    title: label,
    context: `${label} came in as your lowest area this week. Start with something small and specific.`,
    steps: [
        { title: 'Notice the triggers', desc: `Pay attention to what tends to knock your ${label.toLowerCase()} today.` },
        { title: 'Pick one small change', desc: "Don't try to fix everything at once. Choose one five-minute habit." },
        { title: 'Use the library', desc: `Open the ${label.toLowerCase()} modules for practical tools.` }
    ],
    actionText: 'Explore modules'
});

export const getActionPlan = (metricId, label) => ACTION_PLANS[metricId] || genericPlan(label);

// Guided Path node -> its plan. Metric nodes carry metricId/label; recovery
// nodes are keyed by id; the pulse milestone has no plan (it is the action).
export const getActionPlanForNode = (node) => {
    if (!node || node.milestone) return null;
    if (node.metricId) return getActionPlan(node.metricId, node.label);
    return RECOVERY_PLANS[node.id] || null;
};
